import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';

export default function MindMapModal({ isOpen, onClose, mindMapData, sourceText }) {
  const { t } = useI18n();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen && mindMapData?.nodes) {
      const timer = setTimeout(() => {
        setExpandedNodes(new Set(mindMapData.nodes.map(n => n.id)));
        setZoom(1);
        setPan({ x: 0, y: 0 });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mindMapData]);

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.3, Math.min(3, prev * delta)));
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.target === svgRef.current || e.target.tagName === 'g') {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  if (!isOpen || !mindMapData) return null;

  const { title, nodes = [] } = mindMapData;
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#d946ef', '#ef4444', '#06b6d4', '#8b5cf6'];

  // Calculate layout
  const centerX = 400;
  const centerY = 300;
  const branchRadius = 180;
  const childRadius = 100;

  const visibleNodes = nodes.filter(n => expandedNodes.has(n.id));
  const angleStep = visibleNodes.length > 0 ? (2 * Math.PI) / visibleNodes.length : 0;

  return (
    <div className="mindmap-overlay" onClick={onClose}>
      <div className="mindmap-modal" onClick={e => e.stopPropagation()}>
        <div className="mindmap-header">
          <h3>{t('mindMapTitle')}: {title}</h3>
          <div className="mindmap-controls">
            <button className="mindmap-zoom-btn" onClick={() => setZoom(prev => Math.min(3, prev * 1.2))} title={t('zoomIn')}>+</button>
            <button className="mindmap-zoom-btn" onClick={() => setZoom(prev => Math.max(0.3, prev / 1.2))} title={t('zoomOut')}>−</button>
            <button className="mindmap-zoom-btn" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title={t('reset')}>⟲</button>
            <button className="mindmap-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>
        
        <div 
          className="mindmap-canvas" 
          ref={containerRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <svg 
            ref={svgRef}
            width="100%" 
            height="100%" 
            viewBox="0 0 800 600"
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center center' }}
          >
            {/* Background grid */}
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(148,163,184,0.1)" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="800" height="600" fill="url(#grid)" />

            {/* Central node */}
            <g className="mindmap-center">
              <circle cx={centerX} cy={centerY} r="50" fill="#6366f1" stroke="#818cf8" strokeWidth="3" />
              <text 
                x={centerX} 
                y={centerY} 
                textAnchor="middle" 
                dominantBaseline="middle" 
                fill="white" 
                fontSize="14" 
                fontWeight="bold"
                style={{ pointerEvents: 'none' }}
              >
                {title.length > 15 ? title.slice(0, 15) + '...' : title}
              </text>
            </g>

            {/* Branch nodes */}
            {visibleNodes.map((node, idx) => {
              const angle = idx * angleStep - Math.PI / 2;
              const branchX = centerX + Math.cos(angle) * branchRadius;
              const branchY = centerY + Math.sin(angle) * branchRadius;
              const color = colors[idx % colors.length];
              const isExpanded = expandedNodes.has(node.id);
              const childCount = (node.children || []).length;

              return (
                <g key={node.id}>
                  {/* Connection line to center */}
                  <line 
                    x1={centerX} y1={centerY} 
                    x2={branchX} y2={branchY} 
                    stroke={color} 
                    strokeWidth="2" 
                    opacity="0.6"
                  />
                  
                  {/* Branch node */}
                  <g 
                    className="mindmap-node" 
                    onClick={() => childCount > 0 && toggleNode(node.id)}
                    style={{ cursor: childCount > 0 ? 'pointer' : 'default' }}
                  >
                    <rect 
                      x={branchX - 70} 
                      y={branchY - 20} 
                      width="140" 
                      height="40" 
                      rx="20" 
                      fill={color} 
                      opacity="0.9"
                      stroke="white"
                      strokeWidth="2"
                    />
                    <text 
                      x={branchX} 
                      y={branchY} 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill="white" 
                      fontSize="12" 
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      {node.label.length > 20 ? node.label.slice(0, 20) + '...' : node.label}
                    </text>
                    {childCount > 0 && (
                      <circle 
                        cx={branchX + 75} 
                        cy={branchY} 
                        r="10" 
                        fill="white" 
                        stroke={color}
                        strokeWidth="2"
                      />
                    )}
                    {childCount > 0 && (
                      <text 
                        x={branchX + 75} 
                        y={branchY} 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={color} 
                        fontSize="10" 
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        {isExpanded ? '−' : '+'}
                      </text>
                    )}
                  </g>

                  {/* Child nodes */}
                  {isExpanded && (node.children || []).map((child, cidx) => {
                    const childAngle = angle + (cidx - (childCount - 1) / 2) * 0.35;
                    const childX = branchX + Math.cos(childAngle) * childRadius;
                    const childY = branchY + Math.sin(childAngle) * childRadius;

                    return (
                      <g key={child.id}>
                        <line 
                          x1={branchX} y1={branchY} 
                          x2={childX} y2={childY} 
                          stroke={color} 
                          strokeWidth="1.5" 
                          opacity="0.4"
                        />
                        <rect 
                          x={childX - 55} 
                          y={childY - 16} 
                          width="110" 
                          height="32" 
                          rx="16" 
                          fill={`${color}20`}
                          stroke={color}
                          strokeWidth="1.5"
                        />
                        <text 
                          x={childX} 
                          y={childY} 
                          textAnchor="middle" 
                          dominantBaseline="middle" 
                          fill={color} 
                          fontSize="11"
                          style={{ pointerEvents: 'none' }}
                        >
                          {child.label.length > 25 ? child.label.slice(0, 25) + '...' : child.label}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>

        <div className="mindmap-footer">
          <span className="mindmap-hint">
            {t('scrollToZoom')} • {t('dragToPan')} • {t('clickBranchesToExpandCollapse')}
          </span>
          {sourceText && (
            <button 
              className="mindmap-source-btn" 
              onClick={() => {
                const el = document.createElement('textarea');
                el.value = sourceText;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                alert(t('sourceTextCopied'));
              }}
            >
              {t('copySource')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
